/**
 * useNarratorReactions Hook
 *
 * Manages live narrator text transitions during the rewrite session.
 * Selects curated reactions based on user actions (accept, score crossing,
 * rescoring, session complete) and tracks shown reactions for dedup.
 *
 * Rescore timing logic:
 * - If rescore takes <500ms: skip loading reaction, go straight to result
 * - If rescore takes >500ms: show loading reaction, then cross-fade to result
 * - If rescore takes >15s (timeout): show rescore_failure reaction
 * - If rescore completes (isRescoring true→false): show rescore_result
 *
 * Default state: show narratorIntro. Reactions override it during session activity.
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import type {
  ReactionTrigger,
  EnergyLevel,
  VoiceTier,
  SeverityBucket,
  CrossingTarget,
} from './types';
import { selectReaction, getEnergyFromDelta } from './selectReaction';
import { getCategoryFromScore } from '@/lib/categoryStyles';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  captureNarratorReactionShown,
  captureNarratorSessionSummary,
} from '@/lib/analytics/events/narrator-events';

import type { TierId } from '@dramadetector/shared';

/** Tier string accepted by this hook (superset of TierId to include family_member) */
type TierInput = TierId | 'family_member';

/** Delay before showing loading reaction (allows fast rescores to skip it) */
const LOADING_DELAY_MS = 500;

/** Maximum time (ms) to wait for a rescore before treating it as a failure */
const RESCORE_TIMEOUT_MS = 15_000;

/** Valid severity values for accept reactions */
const VALID_SEVERITIES: ReadonlySet<string> = new Set(['critical', 'high', 'medium', 'low']);

/** Map tier string to VoiceTier */
function toVoiceTier(tier: TierInput): VoiceTier {
  switch (tier) {
    case 'anonymous':
    case 'free':
      return 'l1';
    case 'drama_plus':
    case 'family_member':
      return 'l2';
    case 'dramax':
      return 'l3';
    default: {
      const _exhaustive: never = tier;
      return 'l1';
    }
  }
}

export interface UseNarratorReactionsOptions {
  /** User subscription tier */
  tier: TierInput;
  /** LLM-generated narrator intro (shown as default) */
  narratorIntro: string | null;
  /** Original analysis score (0-10 scale) */
  originalScore: number;
  /** Current revised score (0-10 scale, null before first rescore) */
  revisedScore: number | null;
  /** Current cumulative score delta */
  scoreDelta: number;
  /** Number of accepted rewrites so far */
  acceptedCount: number;
  /** Whether a rescore is in progress */
  isRescoring: boolean;
  /** Whether the session is complete (all issues resolved) */
  sessionComplete: boolean;
  /** Severity of the last accepted issue */
  lastAcceptedSeverity?: string;
}

export interface UseNarratorReactionsReturn {
  /** Text to display in the narrator card */
  currentText: string;
  /** Current energy level for animation intensity */
  energyLevel: EnergyLevel;
  /** What triggered the current text */
  trigger: 'intro' | ReactionTrigger;
  /** True when the session is complete (maps to NarratorReactionCard isWrapUp prop) */
  isWrapUp: boolean;
}

/**
 * Hook that selects and manages narrator reactions during a rewrite session.
 *
 * Lifecycle:
 * 1. Default: shows narratorIntro
 * 2. On accept: shows accept reaction (severity + tier + delta based)
 * 3. On score crossing category boundary: shows crossing reaction
 * 4. On rescoring: shows loading reaction (after 500ms delay)
 * 5. On rescore complete: shows result reaction with {newScore}
 * 6. On rescore failure: shows failure fallback
 * 7. On session complete: shows complete reaction with template vars
 */
export function useNarratorReactions({
  tier,
  narratorIntro,
  originalScore,
  revisedScore,
  scoreDelta,
  acceptedCount,
  isRescoring,
  sessionComplete,
  lastAcceptedSeverity,
}: UseNarratorReactionsOptions): UseNarratorReactionsReturn {
  const narratorScoreChaseEnabled = useFeatureFlag('narrator_score_chase_enabled');

  const voiceTier = toVoiceTier(tier);
  const shownKeys = useRef(new Set<string>());
  const prevAcceptedCount = useRef(0);
  const prevCategory = useRef(getCategoryFromScore(originalScore));
  const prevIsRescoring = useRef(false);

  // Timing refs for rescore loading delay
  const rescoreStartTime = useRef<number>(0);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rescoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rescoreTimedOutRef = useRef(false);
  const scoreAtRescoreStart = useRef<number>(0);
  const sessionCompleteShownRef = useRef(false);

  // Analytics tracking refs
  const reactionsShownCountRef = useRef(0);
  const sessionStartTimeRef = useRef(Date.now());
  // Use ref for scoreDelta so showReaction doesn't cascade effects on delta changes
  const scoreDeltaRef = useRef(scoreDelta);
  scoreDeltaRef.current = scoreDelta;

  const [currentText, setCurrentText] = useState(narratorIntro ?? '');
  const [activeTrigger, setActiveTrigger] = useState<'intro' | ReactionTrigger>('intro');

  const energyLevel = useMemo(
    () => getEnergyFromDelta(scoreDelta),
    [scoreDelta],
  );

  // Keep a ref to current energyLevel so showReaction can read it without
  // being added to its dependency array (avoids cascading re-renders)
  const energyLevelRef = useRef(energyLevel);
  energyLevelRef.current = energyLevel;

  /** Show a reaction (helper to reduce duplication) */
  const showReaction = useCallback(
    (trigger: ReactionTrigger, extraCtx?: Partial<Parameters<typeof selectReaction>[0]>) => {
      const result = selectReaction(
        {
          trigger,
          tier: voiceTier,
          cumulativeDelta: scoreDeltaRef.current,
          ...extraCtx,
        },
        shownKeys.current,
      );
      if (result) {
        shownKeys.current.add(result.reaction.key);
        setCurrentText(result.text);
        setActiveTrigger(trigger);

        // Track each reaction shown
        reactionsShownCountRef.current += 1;
        captureNarratorReactionShown({
          trigger,
          tier,
          energy_level: energyLevelRef.current,
          cumulative_delta: scoreDeltaRef.current,
          reaction_index: reactionsShownCountRef.current,
          is_card_visible: true, // TODO: wire to actual card visibility state
        });
      }
    },
    [voiceTier, tier],
  );

  // Reset when narratorIntro changes (new analysis)
  useEffect(() => {
    if (narratorIntro) {
      setCurrentText(narratorIntro);
      setActiveTrigger('intro');
      shownKeys.current.clear();
      prevAcceptedCount.current = 0;
      prevCategory.current = getCategoryFromScore(originalScore);
      prevIsRescoring.current = false;
      rescoreStartTime.current = 0;
      scoreAtRescoreStart.current = 0;
      sessionCompleteShownRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (rescoreTimeoutRef.current) {
        clearTimeout(rescoreTimeoutRef.current);
        rescoreTimeoutRef.current = null;
      }
    }
  }, [narratorIntro, originalScore]);

  // Detect accept events (acceptedCount increases)
  useEffect(() => {
    if (acceptedCount > prevAcceptedCount.current && acceptedCount > 0) {
      prevAcceptedCount.current = acceptedCount;

      const severity: SeverityBucket = VALID_SEVERITIES.has(lastAcceptedSeverity ?? '')
        ? (lastAcceptedSeverity as SeverityBucket)
        : 'medium';
      showReaction('accept', { severity });
    }
  }, [acceptedCount, lastAcceptedSeverity, showReaction]);

  // Detect score category crossings
  useEffect(() => {
    if (revisedScore === null) return;

    const newCategory = getCategoryFromScore(revisedScore);
    if (newCategory !== prevCategory.current && scoreDelta > 0) {
      prevCategory.current = newCategory;

      let crossingTarget: CrossingTarget;
      if (newCategory === 'safe') crossingTarget = 'safe';
      else if (newCategory === 'caution') crossingTarget = 'caution';
      else crossingTarget = 'high_drama';

      showReaction('crossing', { crossingTarget });
    }
  }, [revisedScore, scoreDelta, showReaction]);

  // Detect rescoring state transitions with timing logic
  useEffect(() => {
    const wasRescoring = prevIsRescoring.current;
    prevIsRescoring.current = isRescoring;

    if (isRescoring && !wasRescoring) {
      // Rescore started: record start time and score snapshot
      rescoreStartTime.current = Date.now();
      scoreAtRescoreStart.current = revisedScore ?? 0;

      // Set a 500ms timer to show loading reaction
      // If rescore completes before this fires, we skip loading
      loadingTimerRef.current = setTimeout(() => {
        loadingTimerRef.current = null;
        // Only show loading if still rescoring when timer fires
        if (prevIsRescoring.current) {
          showReaction('loading');
        }
      }, LOADING_DELAY_MS);

      // Safety net: if rescore is still in progress after RESCORE_TIMEOUT_MS,
      // treat it as a failure and show the failure reaction
      rescoreTimedOutRef.current = false;
      rescoreTimeoutRef.current = setTimeout(() => {
        rescoreTimeoutRef.current = null;
        if (prevIsRescoring.current) {
          rescoreTimedOutRef.current = true;
          showReaction('rescore_failure');
        }
      }, RESCORE_TIMEOUT_MS);
    }

    if (!isRescoring && wasRescoring) {
      // Rescore ended: clear loading timer if still pending
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      // Clear failure timeout — rescore completed before timeout
      if (rescoreTimeoutRef.current) {
        clearTimeout(rescoreTimeoutRef.current);
        rescoreTimeoutRef.current = null;
      }

      // Show rescore result only if the timeout hasn't already fired rescore_failure.
      // Without this guard, a slow API response (>15s) would show failure then success.
      if (!rescoreTimedOutRef.current) {
        showReaction('rescore_result', {
          templateVars: { newScore: Math.round(revisedScore ?? 0) },
        });
      }
    }
  }, [isRescoring, revisedScore, showReaction]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (rescoreTimeoutRef.current) {
        clearTimeout(rescoreTimeoutRef.current);
      }
    };
  }, []);

  // Detect session complete (one-shot: only fires once per session)
  useEffect(() => {
    if (sessionComplete && scoreDelta > 0 && !sessionCompleteShownRef.current) {
      sessionCompleteShownRef.current = true;
      showReaction('complete', {
        templateVars: {
          original: Math.round(originalScore),
          revised: Math.round(revisedScore ?? 0),
          delta: Math.round(scoreDelta),
          acceptedCount,
        },
      });
    }
  }, [sessionComplete, scoreDelta, originalScore, revisedScore, showReaction]);

  // Emit session summary once when session completes.
  // NOTE: Reads energyLevel, scoreDelta, acceptedCount from closure — these may be stale
  // if React batches the sessionComplete update with other state changes. This is acceptable
  // for analytics (approximate values are fine). The eslint-disable is intentional: we only
  // want this to fire once when sessionComplete transitions to true.
  useEffect(() => {
    if (!sessionComplete || !sessionCompleteShownRef.current) return;
    captureNarratorSessionSummary({
      reactions_shown_count: reactionsShownCountRef.current,
      pills_shown_count: 0, // pills count is owned by NarratorFloatingPill
      pills_tapped_count: 0,
      energy_peak_reached: energyLevel,
      session_duration_ms: Date.now() - sessionStartTimeRef.current,
      total_delta: Math.round(scoreDelta),
      rewrites_accepted: acceptedCount,
      rewrites_total: acceptedCount, // caller provides accepted; total unknown here
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionComplete]);

  // Feature flag: when disabled, fall back to static narratorIntro.
  // IMPORTANT: This early return MUST stay below all hooks — moving it higher would
  // violate React's rules of hooks (conditional hook execution).
  if (narratorScoreChaseEnabled === false) {
    return {
      currentText: narratorIntro ?? '',
      energyLevel: 'calm' as const,
      trigger: 'intro' as const,
      isWrapUp: false,
    };
  }

  return {
    currentText,
    energyLevel,
    trigger: activeTrigger,
    isWrapUp: activeTrigger === 'complete',
  };
}
